import { clsx } from 'clsx';
import { Tooltip } from '@/components/ui';

interface VesselBadgeProps {
  // Fixture badges
  isOwnFixture?: boolean | number;
  scrubberInstalled?: boolean;
  age15Plus?: boolean;
  onSubs?: boolean | number;
  // Position list badges
  nextDryDockWarning?: boolean | number;
  market?: boolean | number;
  shownToMarket?: boolean | number;
  onHoldWithCargo?: boolean | number;
  onHoldWithCargoLabel?: string;
  onHoldWithoutCargo?: boolean | number;
  sireWarning?: boolean | number;
  onObservation?: boolean | number;
  narrowLaycan?: boolean | number;
  onRestrictionList?: boolean | number;
  // Cargo list badges
  statusPossible?: boolean;
  privacyStealth?: boolean;
  privacySuperSensitive?: boolean;
  inline?: boolean;
  /** Smaller circles (e.g. compact mobile rows) */
  compact?: boolean;
  /** No left margin (use when parent uses flex gap) */
  flushLeft?: boolean;
  className?: string;
}

/**
 * VesselBadge component matching Angular implementation
 * Displays circular overlapping badges for vessel properties with tooltips
 * Supports both Recent Fixtures and Position List use cases
 */
export function VesselBadge({
  isOwnFixture,
  scrubberInstalled,
  age15Plus,
  onSubs,
  nextDryDockWarning,
  market,
  shownToMarket,
  onHoldWithCargo,
  onHoldWithCargoLabel = 'On Hold with Cargo',
  onHoldWithoutCargo,
  sireWarning,
  onObservation,
  narrowLaycan,
  onRestrictionList,
  statusPossible,
  privacyStealth,
  privacySuperSensitive,
  inline = false,
  compact = false,
  flushLeft = false,
  className = '',
}: VesselBadgeProps) {
  // Helper to check boolean or number (0/1)
  const isActive = (val?: boolean | number) => val === true || val === 1;

  const hasOwnFixture = isActive(isOwnFixture);
  const hasOnSubs = isActive(onSubs);
  const hasNextDryDock = isActive(nextDryDockWarning);
  const hasShownToMarket = isActive(shownToMarket) || isActive(market);
  const hasHoldWithCargo = isActive(onHoldWithCargo);
  const hasHoldWithoutCargo = isActive(onHoldWithoutCargo);
  const hasSireWarning = isActive(sireWarning);
  const hasOnObservation = isActive(onObservation);
  const hasNarrowLaycan = isActive(narrowLaycan);
  const hasRestrictions = isActive(onRestrictionList);

  // Don't render if no badges to show
  const hasBadges =
    hasOwnFixture ||
    scrubberInstalled ||
    age15Plus ||
    hasOnSubs ||
    hasNextDryDock ||
    hasShownToMarket ||
    hasHoldWithCargo ||
    hasHoldWithoutCargo ||
    hasSireWarning ||
    hasOnObservation ||
    hasNarrowLaycan ||
    hasRestrictions ||
    statusPossible ||
    privacyStealth ||
    privacySuperSensitive;

  if (!hasBadges) {
    return null;
  }

  const badgeSize = compact ? 'h-5 w-5' : 'h-6 w-6';
  const badgeOverlap = compact ? '-ml-2' : '-ml-2.5';
  const badgeBaseClasses = clsx(
    badgeSize,
    'rounded-full border border-white cursor-pointer bg-cover bg-center transition-transform hover:z-10 hover:scale-110 hover:shadow-lg',
    !compact && 'hover:scale-125'
  );

  // Build badges array in Angular's order
  const badges: Array<{
    key: string;
    show: boolean;
    color: string;
    label: string;
    image?: string;
    imageBadge?: boolean;
  }> = [
    { key: 'onSubs', show: hasOnSubs, color: '#ffeb3b', label: 'On Subs' },
    { key: 'nextDryDock', show: hasNextDryDock, color: '#359B08', label: 'Next Dry Dock Coming' },
    { key: 'shownToMarket', show: hasShownToMarket, color: '#232323', label: 'Shown to Market' },
    { key: 'holdWithCargo', show: hasHoldWithCargo, color: '#0047ff', label: onHoldWithCargoLabel },
    {
      key: 'holdNoCargo',
      show: hasHoldWithoutCargo,
      color: '#f44336',
      label: 'On Hold without Cargo',
    },
    { key: 'sireWarning', show: hasSireWarning, color: '#9e9e9e', label: 'SIRE Warning' },
    { key: 'onObservation', show: hasOnObservation, color: '#673ab7', label: 'On Observation' },
    { key: 'narrowLaycan', show: hasNarrowLaycan, color: '#ff00aa', label: 'Narrow Laycan' },
    {
      key: 'scrubber',
      show: !!scrubberInstalled,
      color: '#f5a623',
      label: 'Scrubber Installed',
      image: '/assets/images/scrubber.png',
    },
    {
      key: 'age15Plus',
      show: !!age15Plus,
      color: '#4CAF50',
      label: '+15 Years Old',
      image: '/assets/images/fifteen-plus.svg',
    },
    {
      key: 'ownFixture',
      show: hasOwnFixture,
      color: '#fff',
      label: 'Tankers UK',
      image: '/assets/images/tankers-uk.svg',
    },
    { key: 'restrictions', show: hasRestrictions, color: '#f44336', label: 'Trading Restrictions' },
    // Cargo list specific badges
    {
      key: 'statusPossible',
      show: !!statusPossible,
      color: '#001b89',
      label: 'Possible',
      image: '/assets/images/badge/possible.png',
      imageBadge: true,
    },
    {
      key: 'privacySuperSensitive',
      show: !!privacySuperSensitive,
      color: '#890000',
      label: 'Super Sensitive',
      image: '/assets/images/badge/super-sensitive.png',
      imageBadge: true,
    },
    {
      key: 'privacyStealth',
      show: !!privacyStealth,
      color: '#cd6a00',
      label: 'Stealth',
      image: '/assets/images/badge/stealth.png',
      imageBadge: true,
    },
  ];

  const activeBadges = badges.filter((b) => b.show);

  return (
    <div
      className={clsx(
        'relative flex flex-shrink-0',
        compact ? 'h-5' : 'h-6',
        flushLeft ? 'ml-0' : inline ? (compact ? 'ml-0.5' : 'ml-1.5') : compact ? 'ml-2' : 'ml-4',
        className
      )}
    >
      {activeBadges.map((badge, index) => (
        <Tooltip key={badge.key} content={badge.label}>
          <div
            className={clsx(badgeBaseClasses, index > 0 && badgeOverlap)}
            aria-label={badge.label}
            style={{
              backgroundColor: badge.color,
              ...(badge.image && {
                backgroundImage: `url(${badge.image})`,
                backgroundSize: badge.imageBadge ? '65%' : '100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }),
            }}
          />
        </Tooltip>
      ))}
    </div>
  );
}
